import { ChevronDown, ChevronUp } from "lucide-react";
import { useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useOverflow } from "@/hooks/use-overflow";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { isEmpty } from "@/utils/util";

const previewLines = 6;

function exceedsPreview(element: HTMLDivElement): boolean {
  const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight);
  return element.scrollHeight > lineHeight * previewLines + 1;
}

export function TicketBody({ body, className }: { body: string | null; className?: string }) {
  const { t } = useI18n();
  const bodyId = useId();
  const [contentRef, canExpand] = useOverflow<HTMLDivElement>({ measure: exceedsPreview });
  const viewportRef = useRef<HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const isExpanded = canExpand && expanded;

  return (
    <div className={cn("min-w-0", className)}>
      {isEmpty(body) ? (
        <p className="text-sm text-muted-foreground italic">{t("tickets.detail.noBody")}</p>
      ) : (
        <div className="relative">
          <section
            aria-label={t("tickets.detail.bodyLabel")}
            className={cn(
              /* 这里 36 是 预览行数 * 下方的 leading-6 计算的 */
              "max-h-36 overflow-hidden",
              canExpand && !isExpanded && "ticket-body-preview",
              isExpanded && "max-h-60 overflow-y-auto overscroll-contain",
            )}
            id={bodyId}
            ref={viewportRef}
            tabIndex={isExpanded ? 0 : undefined}
          >
            <div className="whitespace-pre-wrap wrap-anywhere leading-6 text-foreground" ref={contentRef}>
              {body}
            </div>
          </section>
          {canExpand ? (
            <Button
              aria-controls={bodyId}
              aria-expanded={isExpanded}
              className={cn("px-0", isExpanded ? "mt-1" : "absolute inset-e-0 bottom-0 w-32 max-w-full justify-end")}
              size="xs"
              type="button"
              variant="link"
              onClick={() => {
                if (viewportRef.current) viewportRef.current.scrollTop = 0;
                setExpanded(!isExpanded);
              }}
            >
              {t(isExpanded ? "tickets.detail.collapseBody" : "tickets.detail.expandBody")}
              {isExpanded ? <ChevronUp data-icon="inline-end" /> : <ChevronDown data-icon="inline-end" />}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
